import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Paper,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  AppBar,
  Toolbar,
  Container
} from '@mui/material';
import { 
  AutoFixHigh as MagicIcon, 
  Description as CVIcon,
  WorkOutline as JobIcon,
  UploadFile as UploadIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  PictureAsPdf as PdfIcon,
  Article as WordIcon,
  Visibility as PreviewIcon,
  FormatBold as BoldIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useCVStore } from '../../store/useCVStore';
import { useCVAdaptation } from '../../hooks/useCVAdaptation';
import { adminColors, textFieldStyles, buttonStyles, selectMenuProps } from './adminStyles';
import * as pdfjs from 'pdfjs-dist';
import mammoth from 'mammoth';
import { supabase } from '../../lib/supabase';
import { pdf } from '@react-pdf/renderer';
import CVDocument from './CVDocument';
import CVPreview from './CVPreview';
import { parseCVText, sectionsToText } from './cvParser';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, BorderStyle, ShadingType } from 'docx';
import { saveAs } from 'file-saver';

import CLDocument from './CLDocument';
import InterviewScriptDocument from './InterviewScriptDocument';
import ReactMarkdown from 'react-markdown';

// Load worker locally from the public folder to avoid CDN fetch issues
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const AdminCVAdaptation = ({ isGuest = false }) => {
  const isGuestMode = isGuest || localStorage.getItem('isGuest') === 'true';
  const { 
    originalText, 
    setOriginalText, 
    jobDescription, 
    setJobDescription, 
    adaptedText, 
    setAdaptedText,
    isGenerating, 
    error,
    setError
  } = useCVStore();

  const { adaptCV } = useCVAdaptation();

  const [customInstructions, setCustomInstructions] = useState('');
  const [isParsing, setIsParsing] = useState(false);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [cvLibrary, setCVLibrary] = useState([]);
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [selectedCVId, setSelectedCVId] = useState('');
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isSaveCVDialogOpen, setIsSaveCVDialogOpen] = useState(false);
  const [newPromptName, setNewPromptName] = useState('');
  const [newCVName, setNewCVName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [language, setLanguage] = useState('fr');
  const [coverLetterFR, setCoverLetterFR] = useState('');
  const [coverLetterEN, setCoverLetterEN] = useState('');
  const [isGeneratingCL, setIsGeneratingCL] = useState(false);
  const [prospectingText, setProspectingText] = useState('');
  const [isGeneratingProspecting, setIsGeneratingProspecting] = useState(false);
  const [prospectingType, setProspectingType] = useState('cdi');
  const [directResponseText, setDirectResponseText] = useState('');
  const [isGeneratingDirectResponse, setIsGeneratingDirectResponse] = useState(false);
  const [receivedMessage, setReceivedMessage] = useState('');
  const [qaText, setQaText] = useState('');
  const [isGeneratingQA, setIsGeneratingQA] = useState(false);
  const [qaQuestions, setQaQuestions] = useState('');
  const [interviewScriptText, setInterviewScriptText] = useState('');
  const [isGeneratingInterviewScript, setIsGeneratingInterviewScript] = useState(false);
  const [interviewerType, setInterviewerType] = useState('Recruteur RH');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [showInterviewScriptPreview, setShowInterviewScriptPreview] = useState(false);
  const [objectionResponseText, setObjectionResponseText] = useState('');
  const [isGeneratingObjection, setIsGeneratingObjection] = useState(false);
  const [objectionMessage, setObjectionMessage] = useState('');
  const [objectionType, setObjectionType] = useState('auto');

  // Création de contenu states
  const [contentCreationText, setContentCreationText] = useState('');
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const [contentType, setContentType] = useState('responsibilities_benefits');
  const [experienceText, setExperienceText] = useState('');
  const [experienceDates, setExperienceDates] = useState('');
  const [targetJobTitle, setTargetJobTitle] = useState('');
  const [targetAudience, setTargetAudience] = useState('');
  const [clientProblem, setClientProblem] = useState('');
  const [newsContent, setNewsContent] = useState('');
  // Expert/Pro states
  const [activityType, setActivityType] = useState('ia_ml');
  const [topicsAddressed, setTopicsAddressed] = useState('');
  const [publicationObjective, setPublicationObjective] = useState('opinion');

  const [showPreview, setShowPreview] = useState(false);
  const [cvSections, setCvSections] = useState([]);
  const [previewConfig, setPreviewConfig] = useState({
    fontFamily: 'Tahoma',
    fontSize: 10,
    lineHeight: 1.4,
    primaryColor: '#191970',
    secondaryColor: '#000000',
    headingColor: '#191970',
    boldHeadings: true
  });

  // Guard to prevent useEffect from re-parsing when change came from drag reorder
  const skipParseRef = useRef(false);

  // Sync sections when adaptedText changes (e.g. from AI generation or manual edit)
  useEffect(() => {
    if (skipParseRef.current) {
      skipParseRef.current = false;
      return;
    }
    if (adaptedText) {
      setCvSections(parseCVText(adaptedText));
    } else {
      setCvSections([]);
    }
  }, [adaptedText]);

  // When sections are reordered via drag & drop, sync back to text
  const handleSectionsChange = useCallback((newSections) => {
    setCvSections(newSections);
    skipParseRef.current = true;
    setAdaptedText(sectionsToText(newSections));
  }, [setAdaptedText]);

  const handlePromptChange = (event) => {
    const promptId = event.target.value;
    setSelectedPromptId(promptId);
    if (!promptId) {
      setCustomInstructions('');
      return;
    }
    const prompt = savedPrompts.find(p => p.id === promptId);
    if (prompt) {
      setCustomInstructions(prompt.content);
    }
  };

  const handleCVChange = (event) => {
    const cvId = event.target.value;
    setSelectedCVId(cvId);
    if (!cvId) {
      setOriginalText('');
      setJobDescription('');
      setAdaptedText('');
      return;
    }
    const cv = cvLibrary.find(c => c.id === cvId);
    if (cv) {
      setOriginalText(cv.original_text || cv.content || '');
      setJobDescription('');
      setAdaptedText('');
    }
  };
  const fileInputRef = useRef(null);

  const fetchSavedPrompts = useCallback(async () => {
    if (isGuestMode) {
      setSavedPrompts([]);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('ai_prompts')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setSavedPrompts(data || []);
    } catch (err) {
      console.error('Error fetching prompts:', err);
    }
  }, [isGuestMode]);

  const fetchCVLibrary = useCallback(async () => {
    if (isGuestMode) {
      setCVLibrary([]);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('cv_library')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setCVLibrary(data || []);
    } catch (err) {
      console.error('Error fetching CV library:', err);
    }
  }, [isGuestMode]);

  useEffect(() => {
    fetchSavedPrompts();
    fetchCVLibrary();
  }, [fetchSavedPrompts, fetchCVLibrary]);

  const handleSavePrompt = async () => {
    if (isGuestMode) {
      setError("Désolé, la sauvegarde des prompts est désactivée en mode Guest.");
      return;
    }
    if (!newPromptName || !customInstructions) return;
    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('ai_prompts')
        .insert([{ 
          name: newPromptName, 
          content: customInstructions,
          user_id: user?.id
        }]);
      
      if (error) throw error;
      setIsSaveDialogOpen(false);
      setNewPromptName('');
      fetchSavedPrompts();
    } catch (err) {
      setError(`Erreur lors de la sauvegarde du prompt: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveCV = async () => {
    if (isGuestMode) {
      setError("Désolé, la sauvegarde des CV est désactivée en mode Guest.");
      return;
    }
    if (!newCVName || !originalText) return;
    setIsSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('cv_library')
        .insert([{ 
          name: newCVName, 
          adapted_text: adaptedText || '',
          original_text: originalText,
          job_description: jobDescription || '',
          user_id: user?.id
        }]);
      
      if (error) throw error;
      setIsSaveCVDialogOpen(false);
      setNewCVName('');
      fetchCVLibrary();
    } catch (err) {
      setError(`Erreur lors de la sauvegarde du CV: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePrompt = async (id) => {
    if (isGuestMode) return;
    if (!window.confirm('Voulez-vous vraiment supprimer ce prompt ?')) return;
    try {
      const { error } = await supabase
        .from('ai_prompts')
        .delete()
        .eq('id', id);
      if (error) throw error;
      fetchSavedPrompts();
      if (selectedPromptId === id) {
        setSelectedPromptId('');
        setCustomInstructions('');
      }
    } catch (err) {
      setError(`Erreur suppression prompt: ${err.message}`);
    }
  };

  const handleDeleteCV = async (id) => {
    if (isGuestMode) return;
    if (!window.confirm('Voulez-vous vraiment supprimer ce CV ?')) return;
    try {
      const { error } = await supabase
        .from('cv_library')
        .delete()
        .eq('id', id);
      if (error) throw error;
      fetchCVLibrary();
      if (selectedCVId === id) {
        setSelectedCVId('');
        setOriginalText('');
      }
    } catch (err) {
      setError(`Erreur suppression CV: ${err.message}`);
    }
  };

  const handleUpdateCV = async () => {
    if (isGuestMode || !selectedCVId) return;
    setIsSaving(true);
    try {
      const { error } = await supabase
        .from('cv_library')
        .update({ 
          original_text: originalText,
          job_description: jobDescription,
          adapted_text: adaptedText
        })
        .eq('id', selectedCVId);
      if (error) throw error;
      fetchCVLibrary();
      alert('CV mis à jour avec succès');
    } catch (err) {
      setError(`Erreur mise à jour CV: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAdapt = async () => {
    try {
      await adaptCV(customInstructions, language);
    } catch (err) {
      console.error(err);
    }
  };

  const handleGenerateCL = async () => {
    setIsGeneratingCL(true);
    setError(null);
    try {
      // Utilise la fonction dédiée generate-cover-letter
      const { data, error: clError } = await supabase.functions.invoke('generate-cover-letter', {
        body: { 
          cvText: originalText, 
          jobDescription, 
          customInstructions, 
          language: language
        }
      });
      
      if (clError) throw clError;
      
      if (language === 'fr') {
        setCoverLetterFR(data.adaptedContent);
      } else {
        setCoverLetterEN(data.adaptedContent);
      }
    } catch (err) {
      setError(`Erreur génération lettre: ${err.message}`);
    } finally {
      setIsGeneratingCL(false);
    }
  };

  const handleGenerateDirectResponse = async () => {
    if (!receivedMessage) {
      setError("Veuillez saisir le message reçu");
      return;
    }
    setIsGeneratingDirectResponse(true);
    setError(null);
    try {
      const { data, error: drError } = await supabase.functions.invoke('generate-direct-response', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: { 
          cvText: originalText, 
          receivedMessage, 
          customInstructions, 
          language
        }
      });
      if (drError) throw drError;
      setDirectResponseText(data.adaptedContent);
    } catch (err) {
      setError(`Erreur réponse directe: ${err.message}`);
    } finally {
      setIsGeneratingDirectResponse(false);
    }
  };

  const handleGenerateProspecting = async () => {
    setIsGeneratingProspecting(true);
    setError(null);
    try {
      const { data, error: pError } = await supabase.functions.invoke('generate-prospecting', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: { 
          cvText: originalText, 
          jobDescription, 
          customInstructions, 
          language,
          prospectingType
        }
      });
      
      if (pError) throw pError;
      setProspectingText(data.adaptedContent);
    } catch (err) {
      setError(`Erreur prospection: ${err.message}`);
    } finally {
      setIsGeneratingProspecting(false);
    }
  };

  const handleGenerateQA = async () => {
    if (!qaQuestions) {
      setError("Veuillez saisir vos questions");
      return;
    }
    setIsGeneratingQA(true);
    setError(null);
    try {
      const { data, error: qaError } = await supabase.functions.invoke('generate-qa', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: { 
          cvText: originalText, 
          jobDescription, 
          customInstructions, 
          language,
          questions: qaQuestions
        }
      });
      
      if (qaError) throw qaError;
      setQaText(data.adaptedContent);
    } catch (err) {
      setError(`Erreur Q&A: ${err.message}`);
    } finally {
      setIsGeneratingQA(false);
    }
  };

  const handleGenerateInterviewScript = async () => {
    if (!originalText || !jobDescription) {
      setError("CV et offre d'emploi requis pour générer le script");
      return;
    }
    setIsGeneratingInterviewScript(true);
    setError(null);
    try {
      const { data, error: scriptError } = await supabase.functions.invoke('generate-interview-script', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: { 
          cvText: originalText, 
          jobDescription, 
          additionalInfo,
          interviewerType,
          language
        }
      });
      
      if (scriptError) throw scriptError;
      setInterviewScriptText(data.adaptedContent);
    } catch (err) {
      setError(`Erreur génération script: ${err.message}`);
    } finally {
      setIsGeneratingInterviewScript(false);
    }
  };

  const handleGenerateObjection = async () => {
    if (!objectionMessage) {
      setError("Veuillez saisir le message/objection reçu");
      return;
    }
    setIsGeneratingObjection(true);
    setError(null);
    try {
      const { data, error: objError } = await supabase.functions.invoke('generate-objection-response', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: { 
          cvText: originalText, 
          jobDescription,
          objectionMessage,
          objectionType,
          customInstructions, 
          language
        }
      });
      if (objError) throw objError;
      setObjectionResponseText(data.adaptedContent);
    } catch (err) {
      setError(`Erreur réponse objection: ${err.message}`);
    } finally {
      setIsGeneratingObjection(false);
    }
  };

  const handleGenerateContent = async () => {
    if (contentType === 'problem_solution') {
      if (!clientProblem || !targetJobTitle) {
        setError("Veuillez saisir le problème du client et votre métier");
        return;
      }
    } else if (contentType === 'news_updates') {
      if (!newsContent || !targetJobTitle) {
        setError("Veuillez saisir l'actualité et votre métier");
        return;
      }
    } else if (contentType === 'expert_pro') {
      if (!topicsAddressed || !targetJobTitle) {
        setError("Veuillez saisir les sujets abordés et votre métier");
        return;
      }
    } else {
      if (!experienceText || !targetJobTitle) {
        setError("Veuillez saisir votre expérience et votre métier");
        return;
      }
    }
    setIsGeneratingContent(true);
    setError(null);
    try {
      const { data, error: contentError } = await supabase.functions.invoke('generate-linkedin-content', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: { 
          cvText: originalText, 
          experienceText,
          experienceDates,
          targetJobTitle,
          targetAudience,
          clientProblem,
          newsContent,
          contentType,
          customInstructions, 
          language,
          // Expert/Pro fields
          activityType,
          topicsAddressed,
          publicationObjective
        }
      });
      console.log('Content generation response:', data);
      if (contentError) throw contentError;
      if (data?.adaptedContent) {
        setContentCreationText(data.adaptedContent);
      } else if (data?.error) {
        throw new Error(data.error);
      } else {
        throw new Error('Réponse inattendue du serveur');
      }
    } catch (err) {
      console.error('Content generation error:', err);
      setError(`Erreur génération contenu: ${err.message}`);
    } finally {
      setIsGeneratingContent(false);
    }
  };

  // Helper: sanitize job title for use in filenames
  const getJobTitleSlug = () => {
    const slugify = (str) =>
      str
        .trim()
        .replace(/[^a-zA-Z0-9\u00C0-\u024F\s_-]/g, '')
        .replace(/\s+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_|_$/g, '');

    // 1) Explicit targetJobTitle
    if (targetJobTitle && targetJobTitle.trim()) {
      return slugify(targetJobTitle);
    }

    // 2) Extract from adapted CV: the 2nd line is the adapted role title
    //    Format: "Alexandre SANOU\nConsultant Senior AI Lead\n5+ ans..."
    if (adaptedText && adaptedText.trim()) {
      const lines = adaptedText.trim().split('\n').map(l => l.trim()).filter(Boolean);
      // Skip first line (name), take second line as job title
      if (lines.length >= 2) {
        const candidate = lines[1];
        // Heuristic: a job title is 3-8 words, no email, no phone, no URL
        const words = candidate.split(/\s+/);
        if (words.length >= 2 && words.length <= 8 &&
            !candidate.includes('@') && !candidate.includes('http') &&
            !/^\d/.test(candidate) && !candidate.includes('+')) {
          return slugify(candidate);
        }
      }
    }

    // 3) Extract from jobDescription via explicit patterns
    if (jobDescription && jobDescription.trim()) {
      const desc = jobDescription.trim();
      const patterns = [
        /(?:Poste|Titre|Intitulé\s*(?:du\s*poste)?)\s*:\s*(.+)/i,
      ];
      for (const pat of patterns) {
        const match = desc.match(pat);
        if (match && match[1] && match[1].trim().length > 2 && match[1].trim().length < 80) {
          return slugify(match[1]);
        }
      }
    }

    return '';
  };

  const handleExportPDF = async () => {
    try {
      const blob = await pdf(<CVDocument content={adaptedText} config={previewConfig} sections={cvSections} />).toBlob();
      const jobSlug = getJobTitleSlug();
      const prefix = jobSlug ? `CV_${jobSlug}` : 'CV_Adapte';
      saveAs(blob, `${prefix}_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`);
    } catch (err) {
      setError(`Erreur export PDF: ${err.message}`);
    }
  };

  const handleExportWord = async () => {
    try {
      const DARK_BLUE = previewConfig.primaryColor.replace('#', '');
      const BLACK = previewConfig.secondaryColor.replace('#', '');
      const BODY = previewConfig.secondaryColor.replace('#', '');
      const FONT = previewConfig.fontFamily === 'TimesNewRoman' ? 'Times New Roman' : 
                   previewConfig.fontFamily === 'PTSans' ? 'PT Sans' : 
                   previewConfig.fontFamily;
      
      const baseSize = previewConfig.fontSize * 2; // docx uses half-points

      const isContactLine = (line) =>
        line.includes("@") ||
        line.includes("•") ||
        line.includes("http") ||
        /^(my website|mon site|linkedin)/i.test(line.trim());

      const SECTION_KEYWORDS = [
        "PROFESSIONAL EXPERIENCE", "EXPÉRIENCES PROFESSIONNELLES", "EXPÉRIENCE PROFESSIONNELLE",
        "EDUCATION", "FORMATION",
        "COMPÉTENCES CLÉS", "KEY COMPETENCIES",
        "TECHNICAL SKILLS", "COMPÉTENCES TECHNIQUES", "CERTIFICATIONS",
        "PROJETS", "PROJECTS", "LANGUES", "LANGUAGES",
      ];

      const lines = adaptedText.split('\n');
      let headerLineCount = 0;
      let isHeaderDone = false;
      let linkedInSeen = false;

      const children = lines.flatMap((line, lineIndex) => {
        const trimmed = line.trim();
        const cleanLine = trimmed.replace(/\*\*/g, '');
        const prevNonEmptyLine = lines.slice(0, lineIndex).filter(l => l.trim()).slice(-1)[0] || "";

        if (!trimmed) {
          return [new Paragraph({ children: [], spacing: { before: 60, after: 60 } })];
        }

        // ── Header section (name, title, years, contact info, languages) ────────
        if (!isHeaderDone) {
          headerLineCount++;
          const contact = isContactLine(trimmed);
          if (trimmed.toUpperCase().includes("LINKEDIN")) {
            linkedInSeen = true;
          } else if (linkedInSeen && /^(langues?|languages?)\s*:/i.test(trimmed)) {
            isHeaderDone = true;
          } else if (linkedInSeen) {
            isHeaderDone = true;
          }

          const isName = headerLineCount === 1;
          const isTitleOrYears = !contact && headerLineCount <= 3;
          const textColor = contact ? DARK_BLUE : BLACK;

          return [new Paragraph({
            children: [new TextRun({
              text: cleanLine,
              font: FONT,
              bold: isName || isTitleOrYears,
              size: isName ? baseSize + 20 : isTitleOrYears ? baseSize + 6 : baseSize,
              color: textColor,
            })],
            alignment: "center",
            spacing: { before: 40, after: 40 },
          })];
        }

        // ── Section titles ──────────────────────────────────────────────────────
        const SUB_LABEL_PREFIXES = [
          "COMPÉTENCES PROFESSIONNELLES", "PROFESSIONAL SKILLS",
          "TECHNOLOGIES :", "TECHNOLOGIES:", "CONTEXTE :", "CONTEXTE:",
          "CONTEXT :", "CONTEXT:", "RÉALISATIONS CLÉS", "KEY CONTRIBUTIONS",
          "RÉSULTAT", "RESULT",
        ];
        const isSubLabel = SUB_LABEL_PREFIXES.some(sl =>
          cleanLine.trim().toUpperCase().startsWith(sl)
        );
        const isSectionTitle =
          !isSubLabel && (
            SECTION_KEYWORDS.some(kw => cleanLine.trim().toUpperCase().startsWith(kw)) ||
            (cleanLine.trim().length > 3 &&
              cleanLine.trim() === cleanLine.trim().toUpperCase() &&
              !/^[-•*0-9✓]/.test(cleanLine.trim()))
          );

        if (isSectionTitle) {
          return [new Paragraph({
            children: [new TextRun({
              text: cleanLine.trim(),
              font: FONT,
              bold: previewConfig.boldHeadings,
              size: baseSize + 4,
              color: previewConfig.headingColor ? previewConfig.headingColor.replace('#', '') : DARK_BLUE,
            })],
            spacing: { before: 200, after: 80 },
            border: { bottom: { color: previewConfig.headingColor ? previewConfig.headingColor.replace('#', '') : DARK_BLUE, size: 6, style: "single", space: 2 } },
          })];
        }

        // ── Education lines: **Degree** | Institution (must check before | handler) ──
        const educLineMatch = trimmed.match(/^\*\*([^|]+)\*\*\s*\|(.*)/);
        if (educLineMatch) {
          const degreePart = educLineMatch[1].trim();
          const institutionPart = educLineMatch[2].trim();
          return [new Paragraph({
            children: [
              new TextRun({ text: degreePart, font: FONT, bold: true, size: baseSize, color: DARK_BLUE }),
              new TextRun({ text: " | " + institutionPart, font: FONT, size: baseSize, color: BODY }),
            ],
            spacing: { before: 60, after: 20 },
          })];
        }

        // ── Role line (immediately after experience header with |) ───────────────
        const isRoleLine =
          isHeaderDone &&
          prevNonEmptyLine.includes('|') &&
          !trimmed.includes('|') &&
          !isSubLabel &&
          !isSectionTitle &&
          !trimmed.startsWith('✓') &&
          !trimmed.startsWith('-') &&
          !trimmed.startsWith('•') &&
          !trimmed.startsWith('*');
        if (isRoleLine) {
          return [new Paragraph({
            children: [new TextRun({ text: cleanLine, font: FONT, bold: true, size: baseSize + 2, color: DARK_BLUE })],
            spacing: { before: 0, after: 40 },
          })];
        }

        // ── Experience headers (lines with |) — dark blue bold ───────────────────
        if (trimmed.includes('|')) {
          return [new Paragraph({
            children: [new TextRun({ text: cleanLine.trim(), font: FONT, bold: true, size: baseSize + 2, color: DARK_BLUE })],
            spacing: { before: 100, after: 40 },
          })];
        }

        // ── Bold lines: **text** (sub-experience) or **Category:** skills (tech categories) ──
        const hasBold = trimmed.includes('**');
        if (hasBold) {
          const categoryMatch = trimmed.match(/^\*\*(.+?)\*\*\s*(.*)/);
          if (categoryMatch && categoryMatch[2]) {
            const boldPart = categoryMatch[1].trim();
            const regularPart = categoryMatch[2].trim();
            return [new Paragraph({
              children: [
                new TextRun({ text: boldPart, font: FONT, bold: true, size: baseSize, color: BLACK }),
                new TextRun({ text: " " + regularPart, font: FONT, size: baseSize, color: BODY }),
              ],
              spacing: { before: 60, after: 20 },
            })];
          }
          return [new Paragraph({
            children: [new TextRun({
              text: cleanLine.trim(),
              font: FONT,
              bold: true,
              size: baseSize + 2,
              color: BLACK,
            })],
            spacing: { before: 80, after: 20 },
          })];
        }

        // ── Checkmark lines (key competencies) ─────────────────────────────────
        if (trimmed.startsWith("✓")) {
          return [new Paragraph({
            children: [
              new TextRun({ text: "✓ ", font: FONT, bold: true, size: baseSize, color: DARK_BLUE }),
              new TextRun({ text: trimmed.replace(/^✓\s*/, ""), font: FONT, size: baseSize, color: BODY }),
            ],
            spacing: { before: 40, after: 40 },
            indent: { left: 100 },
          })];
        }

        // ── Bullet points ───────────────────────────────────────────────────────
        if (/^[-•*]\s/.test(trimmed)) {
          return [new Paragraph({
            children: [new TextRun({
              text: "• " + cleanLine.replace(/^[-•*]\s*/, ''),
              font: FONT,
              size: baseSize,
              color: BODY,
            })],
            spacing: { before: 40, after: 40 },
            indent: { left: 200 },
          })];
        }

        // ── Regular text ────────────────────────────────────────────────────────
        return [new Paragraph({
          children: [new TextRun({
            text: cleanLine,
            font: FONT,
            size: baseSize,
            color: BODY,
          })],
          spacing: { before: 60, after: 60 },
        })];
      });

      const doc = new Document({
        sections: [{ properties: {}, children }],
      });

      const blob = await Packer.toBlob(doc);
      const jobSlug = getJobTitleSlug();
      const prefix = jobSlug ? `CV_${jobSlug}` : 'CV_Adapte';
      saveAs(blob, `${prefix}_${new Date().toLocaleDateString().replace(/\//g, '-')}.docx`);
    } catch (err) {
      setError(`Erreur export Word: ${err.message}`);
    }
  };

  const handleExportCLPDF = async (content, lang) => {
    try {
      const blob = await pdf(<CLDocument content={content} language={lang} />).toBlob();
      const jobSlug = getJobTitleSlug();
      const prefix = jobSlug ? `Lettre_Motivation_${jobSlug}` : 'Lettre_Motivation';
      saveAs(blob, `${prefix}_${lang.toUpperCase()}_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`);
    } catch (err) {
      setError(`Erreur export PDF Lettre: ${err.message}`);
    }
  };

  const handleExportCLWord = async (content, lang) => {
    try {
      const lines = content.split('\n');
      const children = lines.map(line => {
        const hasBold = line.includes('**');
        const cleanLine = line.replace(/\*\*/g, '');
        return new Paragraph({
          children: [
            new TextRun({
              text: cleanLine,
              bold: hasBold,
              size: 22
            }),
          ],
          spacing: { before: 120, after: 120 },
        });
      });

      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      const blob = await Packer.toBlob(doc);
      const jobSlug = getJobTitleSlug();
      const prefix = jobSlug ? `Lettre_Motivation_${jobSlug}` : 'Lettre_Motivation';
      saveAs(blob, `${prefix}_${lang.toUpperCase()}_${new Date().toLocaleDateString().replace(/\//g, '-')}.docx`);
    } catch (err) {
      setError(`Erreur export Word Lettre: ${err.message}`);
    }
  };

  const handleExportInterviewScriptPDF = async () => {
    try {
      const blob = await pdf(<InterviewScriptDocument content={interviewScriptText} />).toBlob();
      const jobSlug = getJobTitleSlug();
      const prefix = jobSlug ? `Script_Entretien_${jobSlug}` : 'Script_Entretien';
      saveAs(blob, `${prefix}_${new Date().toLocaleDateString().replace(/\//g, '-')}.pdf`);
    } catch (err) {
      setError(`Erreur export PDF Script: ${err.message}`);
    }
  };

  const handleExportInterviewScriptWord = async () => {
    try {
      const TEAL = '0D9488';
      const TEAL_DARK = '134E4A';
      const GRAY = '374151';
      const LIGHT_GRAY = '6B7280';

      const stripEmojiWord = (str) =>
        str.replace(/[\u{1F000}-\u{1FFFF}]|[\u{2600}-\u{27BF}]|[\uFE0F\u20E3]/gu, '').trim();

      const parseInlineRuns = (text, baseColor = GRAY, size = 20) => {
        const clean = stripEmojiWord(text);
        const runs = [];
        const regex = /\*\*(.*?)\*\*/g;
        let lastIndex = 0;
        let match;
        while ((match = regex.exec(clean)) !== null) {
          if (match.index > lastIndex) {
            runs.push(new TextRun({ text: clean.slice(lastIndex, match.index), color: baseColor, size }));
          }
          runs.push(new TextRun({ text: match[1], bold: true, color: baseColor, size }));
          lastIndex = match.index + match[0].length;
        }
        if (lastIndex < clean.length) {
          runs.push(new TextRun({ text: clean.slice(lastIndex), color: baseColor, size }));
        }
        return runs.length > 0 ? runs : [new TextRun({ text: clean, color: baseColor, size })];
      };

      const paragraphs = [];
      for (const line of interviewScriptText.split('\n')) {
        const trimmed = line.trim();

        if (!trimmed) {
          paragraphs.push(new Paragraph({ children: [], spacing: { before: 40, after: 40 } }));
          continue;
        }

        if (trimmed === '---') {
          paragraphs.push(new Paragraph({
            children: [],
            border: { bottom: { color: 'CCCCCC', space: 1, style: BorderStyle.SINGLE, size: 6 } },
            spacing: { before: 160, after: 160 },
          }));
          continue;
        }

        if (line.startsWith('#### ')) {
          paragraphs.push(new Paragraph({
            heading: HeadingLevel.HEADING_4,
            children: [new TextRun({ text: stripEmojiWord(line.slice(5)), bold: true, color: GRAY, size: 21 })],
            spacing: { before: 120, after: 40 },
          }));
          continue;
        }

        if (line.startsWith('### ')) {
          paragraphs.push(new Paragraph({
            heading: HeadingLevel.HEADING_3,
            children: [new TextRun({ text: stripEmojiWord(line.slice(4)), bold: true, color: TEAL_DARK, size: 22 })],
            spacing: { before: 160, after: 60 },
          }));
          continue;
        }

        if (line.startsWith('## ')) {
          paragraphs.push(new Paragraph({
            heading: HeadingLevel.HEADING_2,
            children: [new TextRun({ text: stripEmojiWord(line.slice(3)), bold: true, color: TEAL, size: 26 })],
            spacing: { before: 200, after: 80 },
          }));
          continue;
        }

        if (line.startsWith('# ')) {
          paragraphs.push(new Paragraph({
            heading: HeadingLevel.HEADING_1,
            children: [new TextRun({ text: stripEmojiWord(line.slice(2)), bold: true, color: '1A1A2E', size: 32 })],
            border: { bottom: { color: TEAL, space: 1, style: BorderStyle.SINGLE, size: 12 } },
            spacing: { before: 240, after: 120 },
          }));
          continue;
        }

        if (line.startsWith('> ')) {
          paragraphs.push(new Paragraph({
            children: parseInlineRuns(line.slice(2), TEAL_DARK, 19),
            indent: { left: 400 },
            border: { left: { color: TEAL, space: 8, style: BorderStyle.SINGLE, size: 18 } },
            shading: { type: ShadingType.CLEAR, fill: 'F0FDF4' },
            spacing: { before: 80, after: 80 },
          }));
          continue;
        }

        if (/^  +[-*] /.test(line)) {
          paragraphs.push(new Paragraph({
            children: parseInlineRuns(line.replace(/^  +[-*] /, ''), LIGHT_GRAY, 19),
            bullet: { level: 1 },
            spacing: { before: 40, after: 40 },
          }));
          continue;
        }

        if (line.startsWith('- ') || line.startsWith('* ')) {
          paragraphs.push(new Paragraph({
            children: parseInlineRuns(line.slice(2), GRAY, 20),
            bullet: { level: 0 },
            spacing: { before: 60, after: 60 },
          }));
          continue;
        }

        paragraphs.push(new Paragraph({
          children: parseInlineRuns(line, GRAY, 20),
          spacing: { before: 60, after: 60 },
        }));
      }

      const doc = new Document({ sections: [{ properties: {}, children: paragraphs }] });
      const blob = await Packer.toBlob(doc);
      const jobSlug = getJobTitleSlug();
      const prefix = jobSlug ? `Script_Entretien_${jobSlug}` : 'Script_Entretien';
      saveAs(blob, `${prefix}_${new Date().toLocaleDateString().replace(/\//g, '-')}.docx`);
    } catch (err) {
      setError(`Erreur export Word Script: ${err.message}`);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsParsing(true);
    setError(null);

    try {
      const fileType = file.type;
      const fileName = file.name.toLowerCase();

      if (fileType === 'application/pdf' || fileName.endsWith('.pdf')) {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
        let fullText = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const textContent = await page.getTextContent();
          
          let lastY, text = '';
          for (const item of textContent.items) {
            if (lastY !== undefined && Math.abs(item.transform[5] - lastY) > 5) {
              text += '\n';
            }
            text += item.str;
            lastY = item.transform[5];
          }
          fullText += text + '\n\n';
        }
        setOriginalText(fullText);
      } else if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || fileName.endsWith('.docx')) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        setOriginalText(result.value);
      } else if (fileType === 'text/plain' || fileName.endsWith('.txt')) {
        const text = await file.text();
        setOriginalText(text);
      } else {
        throw new Error('Format de fichier non supporté. Veuillez utiliser PDF, DOCX ou TXT.');
      }
    } catch (err) {
      setError(`Erreur lors de la lecture du fichier: ${err.message}`);
    } finally {
      setIsParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: adminColors.bg.page, minHeight: '100vh' }}>
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <MagicIcon sx={{ color: adminColors.accent.teal, fontSize: 32 }} />
        <Typography variant="h4" sx={{ color: 'white', fontWeight: 700 }}>
          Adaptateur de CV IA
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.2)' }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Paper sx={{ p: 3, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CVIcon sx={{ color: adminColors.accent.teal }} />
                <Typography variant="h6" sx={{ color: 'white' }}>CV Original</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel sx={{ color: adminColors.text.secondary }}>Bibliothèque</InputLabel>
                  <Select
                    value={selectedCVId}
                    label="Bibliothèque"
                    onChange={handleCVChange}
                    sx={textFieldStyles}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value=""><em>Nouveau/Upload</em></MenuItem>
                    {cvLibrary.map((cv) => (
                      <MenuItem key={cv.id} value={cv.id}>{cv.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <input
                  type="file"
                  accept=".pdf,.docx,.txt"
                  style={{ display: 'none' }}
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={isParsing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
                  disabled={isParsing}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ 
                    borderColor: adminColors.accent.teal, 
                    color: adminColors.accent.teal,
                    '&:hover': { borderColor: adminColors.accent.tealLight, bgcolor: 'rgba(15, 163, 163, 0.1)' }
                  }}
                >
                  Charger
                </Button>
                {originalText && (
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {selectedCVId ? (
                      <Tooltip title="Mettre à jour dans la bibliothèque">
                        <IconButton onClick={handleUpdateCV} sx={{ color: adminColors.accent.teal }}>
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Sauvegarder dans la bibliothèque">
                        <IconButton onClick={() => setIsSaveCVDialogOpen(true)} sx={{ color: adminColors.accent.teal }}>
                          <SaveIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {selectedCVId && (
                      <Tooltip title="Supprimer de la bibliothèque">
                        <IconButton onClick={() => handleDeleteCV(selectedCVId)} sx={{ color: '#EF4444' }}>
                          <ClearIcon />
                        </IconButton>
                      </Tooltip>
                    )}

                    <IconButton size="small" onClick={() => {
                      setOriginalText('');
                      setSelectedCVId('');
                      setJobDescription('');
                      setAdaptedText('');
                    }} sx={{ color: adminColors.text.muted }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={10}
              placeholder="Le texte du CV s'affichera ici..."
              value={originalText}
              onChange={(e) => setOriginalText(e.target.value)}
              sx={textFieldStyles}
            />
          </Paper>

          <Paper sx={{ p: 3, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <JobIcon sx={{ color: adminColors.accent.teal }} />
              <Typography variant="h6" sx={{ color: 'white' }}>Offre d'emploi</Typography>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Collez la description du poste ici..."
              value={jobDescription}
              onChange={(e) => setJobDescription(e.target.value)}
              sx={textFieldStyles}
            />
          </Paper>

          <Paper sx={{ p: 3, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>Prompt détaillé</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel sx={{ color: adminColors.text.secondary }}>Favoris</InputLabel>
                  <Select
                    value={selectedPromptId}
                    label="Favoris"
                    onChange={handlePromptChange}
                    sx={textFieldStyles}
                    MenuProps={selectMenuProps}
                  >
                    <MenuItem value=""><em>Aucun</em></MenuItem>
                    {savedPrompts.map((p) => (
                      <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <IconButton 
                  onClick={() => setIsSaveDialogOpen(true)} 
                  disabled={!customInstructions}
                  sx={{ color: adminColors.accent.teal }}
                >
                  <SaveIcon />
                </IconButton>
                {selectedPromptId && (
                  <IconButton 
                    onClick={() => handleDeletePrompt(selectedPromptId)}
                    sx={{ color: '#EF4444' }}
                  >
                    <ClearIcon />
                  </IconButton>
                )}
              </Box>
            </Box>
            <TextField
              fullWidth
              multiline
              rows={6}
              placeholder="Saisissez votre prompt personnalisé ici..."
              value={customInstructions}
              onChange={(e) => setCustomInstructions(e.target.value)}
              sx={textFieldStyles}
            />
          </Paper>

          <Paper sx={{ p: 3, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>Options de génération</Typography>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel sx={{ color: adminColors.text.secondary }}>Langue cible</InputLabel>
                <Select
                  value={language}
                  label="Langue cible"
                  onChange={(e) => setLanguage(e.target.value)}
                  sx={textFieldStyles}
                  MenuProps={selectMenuProps}
                >
                  <MenuItem value="fr">Français</MenuItem>
                  <MenuItem value="en">Anglais</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Paper>

          <Button
            variant="contained"
            size="large"
            disabled={isGenerating || isParsing || !originalText || !jobDescription}
            onClick={handleAdapt}
            startIcon={isGenerating ? <CircularProgress size={20} color="inherit" /> : <MagicIcon />}
            sx={{ ...buttonStyles.primary, height: 56 }}
          >
            Générer le CV adapté
          </Button>

            {!isGuestMode && (
              <>
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>Options de génération de lettre</Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ color: adminColors.text.secondary }}>Langue de la lettre</InputLabel>
                    <Select
                      value={language}
                      label="Langue de la lettre"
                      onChange={(e) => setLanguage(e.target.value)}
                      sx={textFieldStyles}
                      MenuProps={selectMenuProps}
                    >
                      <MenuItem value="fr">Français</MenuItem>
                      <MenuItem value="en">Anglais</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Button
                  variant="outlined"
                  size="large"
                  fullWidth
                  disabled={isGeneratingCL || isParsing || !originalText || !jobDescription}
                  onClick={handleGenerateCL}
                  startIcon={isGeneratingCL ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
                  sx={{ 
                    borderColor: adminColors.accent.teal, 
                    color: adminColors.accent.teal,
                    height: 56,
                    mb: 4,
                    '&:hover': { borderColor: adminColors.accent.tealLight, bgcolor: 'rgba(15, 163, 163, 0.1)' }
                  }}
                >
                  Générer la lettre ({language === 'fr' ? 'FR' : 'EN'})
                </Button>
              </>
            )}

            <Divider sx={{ my: 4, opacity: 0.1 }} />

            {!isGuestMode && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>Prospection & Messages</Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ color: adminColors.text.secondary }}>Langue</InputLabel>
                    <Select
                      value={language}
                      label="Langue"
                      onChange={(e) => setLanguage(e.target.value)}
                      sx={textFieldStyles}
                      MenuProps={selectMenuProps}
                    >
                      <MenuItem value="fr">Français</MenuItem>
                      <MenuItem value="en">Anglais</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel sx={{ color: adminColors.text.secondary }}>Type de prospection</InputLabel>
                    <Select
                      value={prospectingType}
                      label="Type de prospection"
                      onChange={(e) => setProspectingType(e.target.value)}
                      sx={textFieldStyles}
                      MenuProps={selectMenuProps}
                    >
                      <MenuItem value="cdi">CDI → Freelance</MenuItem>
                      <MenuItem value="freelance">Freelance direct</MenuItem>
                      <MenuItem value="cold">Cold Outreach</MenuItem>
                      <MenuItem value="qa">Questions & Réponses (Q&A)</MenuItem>
                      <MenuItem value="direct_response">Réponse Directe</MenuItem>
                      <MenuItem value="interview_script">Script d'Entretien</MenuItem>
                      <MenuItem value="objection">Réponse aux Objections</MenuItem>
                    </Select>
                  </FormControl>
                  
                  {prospectingType === 'qa' ? (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Posez vos questions ici (ex: Quels sont mes points forts ?)"
                        value={qaQuestions}
                        onChange={(e) => setQaQuestions(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                      />
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={isGeneratingQA || !qaQuestions || !originalText}
                        onClick={handleGenerateQA}
                        startIcon={isGeneratingQA ? <CircularProgress size={20} color="inherit" /> : <MagicIcon />}
                        sx={buttonStyles.primary}
                      >
                        Obtenir les réponses
                      </Button>
                    </>
                  ) : prospectingType === 'direct_response' ? (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Collez le message reçu ici..."
                        value={receivedMessage}
                        onChange={(e) => setReceivedMessage(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                      />
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={isGeneratingDirectResponse || isParsing || !receivedMessage}
                        onClick={handleGenerateDirectResponse}
                        startIcon={isGeneratingDirectResponse ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
                        sx={buttonStyles.primary}
                      >
                        Générer la réponse
                      </Button>
                    </>
                  ) : prospectingType === 'interview_script' ? (
                    <>
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel sx={{ color: adminColors.text.secondary }}>Type d'interlocuteur</InputLabel>
                        <Select
                          value={interviewerType}
                          label="Type d'interlocuteur"
                          onChange={(e) => setInterviewerType(e.target.value)}
                          sx={textFieldStyles}
                          MenuProps={selectMenuProps}
                        >
                          <MenuItem value="Recruteur RH">👔 Recruteur RH</MenuItem>
                          <MenuItem value="Manager direct">🧑‍💼 Manager direct</MenuItem>
                          <MenuItem value="CEO-Fondateur">🚀 CEO / Fondateur</MenuItem>
                          <MenuItem value="Chef d'équipe technique">🛠️ Chef d'équipe technique</MenuItem>
                          <MenuItem value="Pair/Collègue">🤝 Pair / Collègue</MenuItem>
                          <MenuItem value="Jury panel mixte">👥 Jury panel mixte</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="Infos complémentaires (ex: entretien avec un CEO startup SaaS, ambiance décontractée, je suis très stressé, reconversion, trou CV…)"
                        value={additionalInfo}
                        onChange={(e) => setAdditionalInfo(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                      />
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={isGeneratingInterviewScript || isParsing || !originalText || !jobDescription}
                        onClick={handleGenerateInterviewScript}
                        startIcon={isGeneratingInterviewScript ? <CircularProgress size={20} color="inherit" /> : <MagicIcon />}
                        sx={buttonStyles.primary}
                      >
                        Générer Script d'Entretien
                      </Button>
                    </>
                  ) : prospectingType === 'objection' ? (
                    <>
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel sx={{ color: adminColors.text.secondary }}>Type d'objection</InputLabel>
                        <Select
                          value={objectionType}
                          label="Type d'objection"
                          onChange={(e) => setObjectionType(e.target.value)}
                          sx={textFieldStyles}
                          MenuProps={selectMenuProps}
                        >
                          <MenuItem value="auto"><em>🤖 Détection automatique (IA)</em></MenuItem>
                          <MenuItem value="manager_confirm">Manager confirme (veut un RDV)</MenuItem>
                          <MenuItem value="colleague_manages">Un collègue gère l'équipe</MenuItem>
                          <MenuItem value="apply_on_platform">Postulez sur notre page carrière</MenuItem>
                          <MenuItem value="no_need_now">Pas de besoin actuellement</MenuItem>
                          <MenuItem value="already_someone">Déjà quelqu'un en poste</MenuItem>
                          <MenuItem value="cdi_only">On ne recrute qu'en CDI</MenuItem>
                          <MenuItem value="bad_experience">Mauvaise expérience freelances</MenuItem>
                          <MenuItem value="location_issue">Problème de localisation/télétravail</MenuItem>
                          <MenuItem value="left_company">Ne travaille plus dans l'entreprise</MenuItem>
                          <MenuItem value="wrong_tech">Pas la bonne technologie</MenuItem>
                          <MenuItem value="wrong_profile">Pas le bon type de profil</MenuItem>
                          <MenuItem value="no_direct_freelance">Processus d'achat (pas de direct)</MenuItem>
                          <MenuItem value="not_my_scope">Pas mon périmètre / Je ne sais pas</MenuItem>
                          <MenuItem value="no_freelancers">On ne travaille pas avec des freelances</MenuItem>
                          <MenuItem value="forwarded_request">Demande transmise en interne</MenuItem>
                          <MenuItem value="interested_prospect">Prospect intéressé par le profil</MenuItem>
                          <MenuItem value="cold_response">Message sec / froid</MenuItem>
                          <MenuItem value="position_filled">Poste n'est plus à pourvoir</MenuItem>
                          <MenuItem value="link_not_working">Lien ou contact ne fonctionne pas</MenuItem>
                          <MenuItem value="no_visibility">Aucune idée / Pas de visibilité</MenuItem>
                          <MenuItem value="all_internal">On fait tout en interne</MenuItem>
                          <MenuItem value="followup">Message de relance</MenuItem>
                        </Select>
                      </FormControl>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Collez le message/objection reçu du prospect..."
                        value={objectionMessage}
                        onChange={(e) => setObjectionMessage(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                      />
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={isGeneratingObjection || !objectionMessage}
                        onClick={handleGenerateObjection}
                        startIcon={isGeneratingObjection ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />}
                        sx={buttonStyles.primary}
                      >
                        Générer la réponse à l'objection
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant="contained"
                      fullWidth
                      disabled={isGeneratingProspecting || isParsing || !originalText}
                      onClick={handleGenerateProspecting}
                      startIcon={isGeneratingProspecting ? <CircularProgress size={20} color="inherit" /> : <MagicIcon />}
                      sx={{ ...buttonStyles.primary, height: 48 }}
                    >
                      Générer Message Prospection
                    </Button>
                  )}
                </Box>

                <Divider sx={{ my: 3, opacity: 0.1, borderStyle: 'dashed' }} />
              </Box>
            )}

            {/* Section Création de contenu */}
            {!isGuestMode && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 600 }}>📝 Création de contenu</Typography>
                  <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel sx={{ color: adminColors.text.secondary }}>Langue</InputLabel>
                    <Select
                      value={language}
                      label="Langue"
                      onChange={(e) => setLanguage(e.target.value)}
                      sx={textFieldStyles}
                      MenuProps={selectMenuProps}
                    >
                      <MenuItem value="fr">Français</MenuItem>
                      <MenuItem value="en">Anglais</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" sx={{ color: adminColors.text.secondary, mb: 2 }}>
                    Génère des posts LinkedIn professionnels pour promouvoir votre expertise. Basé sur vos responsabilités et expériences.
                  </Typography>
                  
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel sx={{ color: adminColors.text.secondary }}>Type de contenu</InputLabel>
                    <Select
                      value={contentType}
                      label="Type de contenu"
                      onChange={(e) => setContentType(e.target.value)}
                      sx={textFieldStyles}
                      MenuProps={selectMenuProps}
                    >
                      <MenuItem value="responsibilities_benefits">📋 Responsabilités / Bénéfices</MenuItem>
                      <MenuItem value="problem_solution">🎯 Problème / Solution</MenuItem>
                      <MenuItem value="news_updates">📰 Actualités / Nouveautés</MenuItem>
                      <MenuItem value="expert_pro">🧠 Réaction d'expert</MenuItem>
                    </Select>
                  </FormControl>
                  
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Ex: Data Scientist, DevOps Engineer, Développeur Python..."
                    value={targetJobTitle}
                    onChange={(e) => setTargetJobTitle(e.target.value)}
                    sx={{ ...textFieldStyles, mb: 2 }}
                    label="Votre métier"
                  />
                  
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Ex: CTO, VP Engineering, Head of Data, Directeur IT..."
                    value={targetAudience}
                    onChange={(e) => setTargetAudience(e.target.value)}
                    sx={{ ...textFieldStyles, mb: 2 }}
                    label="Cible (N+1, N+2, N+3)"
                  />
                  
                  {contentType === 'problem_solution' ? (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Ex: Son équipe data perd 2h/jour sur des tâches manuelles, les pipelines plantent régulièrement, pas de monitoring..."
                        value={clientProblem}
                        onChange={(e) => setClientProblem(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                        label="Problème du client"
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="(Optionnel) Contexte supplémentaire ou expérience similaire..."
                        value={experienceText}
                        onChange={(e) => setExperienceText(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                        label="Contexte supplémentaire (optionnel)"
                      />
                    </>
                  ) : contentType === 'news_updates' ? (
                    <>
                      <TextField
                        fullWidth
                        multiline
                        rows={5}
                        placeholder="Ex: AWS vient d'annoncer une nouvelle fonctionnalité Bedrock... / La version 3.0 de LangChain est sortie..."
                        value={newsContent}
                        onChange={(e) => setNewsContent(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                        label="Actualité à commenter"
                      />
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="(Optionnel) Votre expertise ou angle d'analyse sur cette actualité..."
                        value={experienceText}
                        onChange={(e) => setExperienceText(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                        label="Contexte supplémentaire (optionnel)"
                      />
                    </>
                  ) : contentType === 'expert_pro' ? (
                    <>
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel id="activity-type-label" sx={{ color: adminColors.text.secondary }}>Domaine d'expertise</InputLabel>
                        <Select
                          labelId="activity-type-label"
                          value={activityType}
                          label="Domaine d'expertise"
                          onChange={(e) => setActivityType(e.target.value)}
                          sx={textFieldStyles}
                          MenuProps={selectMenuProps}
                        >
                          <MenuItem value="ia_ml">🤖 IA / Machine Learning</MenuItem>
                          <MenuItem value="data">📊 Data Science / Analytics</MenuItem>
                          <MenuItem value="devops">⚙️ DevOps / Cloud</MenuItem>
                          <MenuItem value="security">🔒 Cybersécurité</MenuItem>
                          <MenuItem value="dev">💻 Développement</MenuItem>
                          <MenuItem value="product">📱 Product / UX</MenuItem>
                          <MenuItem value="tech_general">🚀 Tech général</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        placeholder="Collez ici l'actualité complète (article, post LinkedIn, annonce...) sur laquelle vous voulez réagir en tant qu'expert..."
                        value={topicsAddressed}
                        onChange={(e) => setTopicsAddressed(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                        label="Actualité à analyser"
                      />
                      
                      <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel id="pub-objective-label" sx={{ color: adminColors.text.secondary }}>Angle du post</InputLabel>
                        <Select
                          labelId="pub-objective-label"
                          value={publicationObjective}
                          label="Angle du post"
                          onChange={(e) => setPublicationObjective(e.target.value)}
                          sx={textFieldStyles}
                          MenuProps={selectMenuProps}
                        >
                          <MenuItem value="opinion">💭 Donner mon avis d'expert</MenuItem>
                          <MenuItem value="explain">📚 Vulgariser / Expliquer</MenuItem>
                          <MenuItem value="alert">⚠️ Alerter / Mettre en garde</MenuItem>
                          <MenuItem value="opportunity">💡 Identifier une opportunité</MenuItem>
                          <MenuItem value="debate">🗣️ Lancer un débat</MenuItem>
                        </Select>
                      </FormControl>
                      
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        placeholder="(Optionnel) Votre expérience personnelle sur ce sujet, un cas vécu, une anecdote..."
                        value={experienceText}
                        onChange={(e) => setExperienceText(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                        label="Mon expérience sur le sujet (optionnel)"
                      />
                    </>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Ex: 2022-2024"
                        value={experienceDates}
                        onChange={(e) => setExperienceDates(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                        label="Dates de l'expérience"
                      />
                      
                      <TextField
                        fullWidth
                        multiline
                        rows={6}
                        placeholder="Collez ici votre expérience LinkedIn (responsabilités, missions, réalisations)..."
                        value={experienceText}
                        onChange={(e) => setExperienceText(e.target.value)}
                        sx={{ ...textFieldStyles, mb: 2 }}
                        label="Expérience (copier-coller depuis LinkedIn)"
                      />
                    </>
                  )}
                  
                  <Button
                    variant="contained"
                    fullWidth
                    disabled={isGeneratingContent || (contentType === 'problem_solution' ? !clientProblem : contentType === 'news_updates' ? !newsContent : contentType === 'expert_pro' ? !topicsAddressed : !experienceText) || !targetJobTitle}
                    onClick={handleGenerateContent}
                    startIcon={isGeneratingContent ? <CircularProgress size={20} color="inherit" /> : <MagicIcon />}
                    sx={buttonStyles.primary}
                  >
                    Générer les posts LinkedIn
                  </Button>
                </Box>

                <Divider sx={{ my: 3, opacity: 0.1, borderStyle: 'dashed' }} />
              </Box>
            )}
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Paper sx={{ p: 4, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}`, minHeight: '400px', position: 'relative' }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6" sx={{ color: 'white' }}>CV Adapté</Typography>
                {adaptedText && (
                  <>
                    <Button
                      size="small"
                      startIcon={<PreviewIcon />}
                      onClick={() => setShowPreview(true)}
                      sx={{ 
                        bgcolor: adminColors.accent.teal,
                        color: 'white',
                        '&:hover': { bgcolor: adminColors.accent.tealDark }
                      }}
                    >
                      Aperçu
                    </Button>
                  </>
                )}
              </Box>
              {adaptedText && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={handleExportPDF}
                    sx={{ color: adminColors.accent.teal, borderColor: adminColors.accent.teal }}
                  >
                    Export PDF
                  </Button>
                  <Button 
                    variant="outlined" 
                    size="small" 
                    onClick={handleExportWord}
                    sx={{ color: adminColors.accent.teal, borderColor: adminColors.accent.teal }}
                  >
                    Export Word
                  </Button>
                </Box>
              )}
            </Box>

            <Divider sx={{ mb: 3, opacity: 0.1 }} />
            
            {adaptedText ? (
                <TextField
                  fullWidth
                  multiline
                  value={adaptedText}
                  onChange={(e) => setAdaptedText(e.target.value)}
                  variant="standard"
                  InputProps={{ disableUnderline: true }}
                  sx={{ 
                    '& .MuiInputBase-input': { 
                      color: adminColors.text.primary,
                      fontFamily: 'inherit',
                      lineHeight: 1.6
                    }
                  }}
                />
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '400px', opacity: 0.2 }}>
                <MagicIcon sx={{ fontSize: 80 }} />
              </Box>
            )}
            {isGenerating && (
              <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.7)', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CircularProgress sx={{ color: adminColors.accent.teal }} />
              </Box>
            )}
          </Paper>

          {(coverLetterFR || coverLetterEN || prospectingText || directResponseText || qaText || interviewScriptText || objectionResponseText || contentCreationText) && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {coverLetterFR && (
                <Paper sx={{ p: 4, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" sx={{ color: 'white' }}>Lettre de Motivation (FR)</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Copier">
                        <Button variant="outlined" size="small" onClick={() => navigator.clipboard.writeText(coverLetterFR)} sx={{ color: adminColors.accent.teal, borderColor: adminColors.accent.teal }}>Copier</Button>
                      </Tooltip>
                      <Tooltip title="Exporter PDF">
                        <IconButton size="small" onClick={() => handleExportCLPDF(coverLetterFR, 'fr')} sx={{ color: adminColors.accent.teal }}><PdfIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title="Exporter Word">
                        <IconButton size="small" onClick={() => handleExportCLWord(coverLetterFR, 'fr')} sx={{ color: adminColors.accent.teal }}><WordIcon /></IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2, opacity: 0.1 }} />
                  <TextField
                    fullWidth
                    multiline
                    value={coverLetterFR}
                    onChange={(e) => setCoverLetterFR(e.target.value)}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        color: adminColors.text.primary,
                        fontFamily: 'inherit',
                        lineHeight: 1.6,
                        fontStyle: 'italic',
                        fontSize: '0.9rem'
                      }
                    }}
                  />
                </Paper>
              )}
              {coverLetterEN && (
                <Paper sx={{ p: 4, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" sx={{ color: 'white' }}>Cover Letter (EN)</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Copy">
                        <Button variant="outlined" size="small" onClick={() => navigator.clipboard.writeText(coverLetterEN)} sx={{ color: adminColors.accent.teal, borderColor: adminColors.accent.teal }}>Copy</Button>
                      </Tooltip>
                      <Tooltip title="Export PDF">
                        <IconButton size="small" onClick={() => handleExportCLPDF(coverLetterEN, 'en')} sx={{ color: adminColors.accent.teal }}><PdfIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title="Export Word">
                        <IconButton size="small" onClick={() => handleExportCLWord(coverLetterEN, 'en')} sx={{ color: adminColors.accent.teal }}><WordIcon /></IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2, opacity: 0.1 }} />
                  <TextField
                    fullWidth
                    multiline
                    value={coverLetterEN}
                    onChange={(e) => setCoverLetterEN(e.target.value)}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        color: adminColors.text.primary,
                        fontFamily: 'inherit',
                        lineHeight: 1.6,
                        fontStyle: 'italic',
                        fontSize: '0.9rem'
                      }
                    }}
                  />
                </Paper>
              )}

              {prospectingText && (
                <Paper sx={{ p: 4, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'white' }}>Message de Prospection</Typography>
                    <Button variant="outlined" size="small" onClick={() => navigator.clipboard.writeText(prospectingText)} sx={{ color: adminColors.accent.teal, borderColor: adminColors.accent.teal }}>Copier</Button>
                  </Box>
                  <Divider sx={{ mb: 2, opacity: 0.1 }} />
                  <TextField
                    fullWidth
                    multiline
                    value={prospectingText}
                    onChange={(e) => setProspectingText(e.target.value)}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        color: adminColors.text.primary,
                        fontFamily: 'inherit',
                        lineHeight: 1.6
                      }
                    }}
                  />
                </Paper>
              )}

              {directResponseText && (
                <Paper sx={{ p: 4, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'white' }}>Réponse au Message</Typography>
                    <Button variant="outlined" size="small" onClick={() => navigator.clipboard.writeText(directResponseText)} sx={{ color: adminColors.accent.teal, borderColor: adminColors.accent.teal }}>Copier</Button>
                  </Box>
                  <Divider sx={{ mb: 2, opacity: 0.1 }} />
                  <TextField
                    fullWidth
                    multiline
                    value={directResponseText}
                    onChange={(e) => setDirectResponseText(e.target.value)}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        color: adminColors.text.primary,
                        fontFamily: 'inherit',
                        lineHeight: 1.6
                      }
                    }}
                  />
                </Paper>
              )}

              {qaText && (
                <Paper sx={{ p: 4, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'white' }}>Réponses Q&A</Typography>
                    <Button variant="outlined" size="small" onClick={() => navigator.clipboard.writeText(qaText)} sx={{ color: adminColors.accent.teal, borderColor: adminColors.accent.teal }}>Copier</Button>
                  </Box>
                  <Divider sx={{ mb: 2, opacity: 0.1 }} />
                  <TextField
                    fullWidth
                    multiline
                    value={qaText}
                    onChange={(e) => setQaText(e.target.value)}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        color: adminColors.text.primary,
                        fontFamily: 'inherit',
                        lineHeight: 1.6
                      }
                    }}
                  />
                </Paper>
              )}

              {interviewScriptText && (
                <Paper sx={{ p: 4, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" sx={{ color: 'white' }}>Script d'Entretien</Typography>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button variant="outlined" size="small" onClick={() => navigator.clipboard.writeText(interviewScriptText)} sx={{ color: adminColors.accent.teal, borderColor: adminColors.accent.teal }}>Copier</Button>
                      <Tooltip title="Aperçu formaté">
                        <IconButton size="small" onClick={() => setShowInterviewScriptPreview(true)} sx={{ color: adminColors.accent.teal }}><PreviewIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title="Exporter PDF">
                        <IconButton size="small" onClick={handleExportInterviewScriptPDF} sx={{ color: adminColors.accent.teal }}><PdfIcon /></IconButton>
                      </Tooltip>
                      <Tooltip title="Exporter Word">
                        <IconButton size="small" onClick={handleExportInterviewScriptWord} sx={{ color: adminColors.accent.teal }}><WordIcon /></IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Divider sx={{ mb: 2, opacity: 0.1 }} />
                  <TextField
                    fullWidth
                    multiline
                    value={interviewScriptText}
                    onChange={(e) => setInterviewScriptText(e.target.value)}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        color: adminColors.text.primary,
                        fontFamily: 'inherit',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap'
                      }
                    }}
                  />
                </Paper>
              )}

              {objectionResponseText && (
                <Paper sx={{ p: 4, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'white' }}>Réponse à l'Objection</Typography>
                    <Button variant="outlined" size="small" onClick={() => navigator.clipboard.writeText(objectionResponseText)} sx={{ color: adminColors.accent.teal, borderColor: adminColors.accent.teal }}>Copier</Button>
                  </Box>
                  <Divider sx={{ mb: 2, opacity: 0.1 }} />
                  <TextField
                    fullWidth
                    multiline
                    value={objectionResponseText}
                    onChange={(e) => setObjectionResponseText(e.target.value)}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        color: adminColors.text.primary,
                        fontFamily: 'inherit',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap'
                      }
                    }}
                  />
                </Paper>
              )}

              {contentCreationText && (
                <Paper sx={{ p: 4, bgcolor: adminColors.bg.surface, borderRadius: 3, border: `1px solid ${adminColors.border.default}` }}>
                  <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'white' }}>📝 Posts LinkedIn Générés</Typography>
                    <Button variant="outlined" size="small" onClick={() => navigator.clipboard.writeText(contentCreationText)} sx={{ color: adminColors.accent.teal, borderColor: adminColors.accent.teal }}>Copier tout</Button>
                  </Box>
                  <Divider sx={{ mb: 2, opacity: 0.1 }} />
                  <TextField
                    fullWidth
                    multiline
                    value={contentCreationText}
                    onChange={(e) => setContentCreationText(e.target.value)}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    sx={{ 
                      '& .MuiInputBase-input': { 
                        color: adminColors.text.primary,
                        fontFamily: 'inherit',
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap'
                      }
                    }}
                  />
                </Paper>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── Interview Script Markdown Viewer ─────────────────────────────── */}
      <Dialog
        fullScreen
        open={showInterviewScriptPreview}
        onClose={() => setShowInterviewScriptPreview(false)}
        TransitionComponent={Transition}
        PaperProps={{ sx: { bgcolor: '#f5f5f5' } }}
        sx={{ zIndex: 1300 }}
        keepMounted={false}
      >
        <AppBar sx={{ position: 'sticky', top: 0, bgcolor: adminColors.bg.surface, borderBottom: `1px solid ${adminColors.border.default}`, zIndex: 1100 }}>
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Typography variant="h6" sx={{ color: 'white' }}>
              Script d'Entretien — Aperçu formaté
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigator.clipboard.writeText(interviewScriptText)}
                sx={{ color: adminColors.accent.teal, borderColor: adminColors.accent.teal }}
              >
                Copier
              </Button>
              <Tooltip title="Exporter PDF">
                <IconButton onClick={handleExportInterviewScriptPDF} sx={{ color: adminColors.accent.teal }}>
                  <PdfIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Exporter Word">
                <IconButton onClick={handleExportInterviewScriptWord} sx={{ color: adminColors.accent.teal }}>
                  <WordIcon />
                </IconButton>
              </Tooltip>
              <Button
                color="inherit"
                onClick={() => setShowInterviewScriptPreview(false)}
                sx={{ border: '1px solid rgba(255,255,255,0.3)' }}
              >
                Fermer
              </Button>
            </Box>
          </Toolbar>
        </AppBar>

        <Box sx={{ p: { xs: 2, md: 6 }, display: 'flex', justifyContent: 'center', minHeight: 'calc(100vh - 64px)', overflow: 'auto' }}>
          <Paper elevation={4} sx={{ width: '100%', maxWidth: '860px', bgcolor: 'white', p: { xs: 3, md: 6 }, borderRadius: 2 }}>
            <Box sx={{
              '& h1': { fontSize: '1.6rem', fontWeight: 700, color: '#1a1a2e', mt: 0, mb: 1.5, pb: 1, borderBottom: '2px solid #0d9488' },
              '& h2': { fontSize: '1.25rem', fontWeight: 700, color: '#0d9488', mt: 3, mb: 1 },
              '& h3': { fontSize: '1.05rem', fontWeight: 600, color: '#134e4a', mt: 2.5, mb: 0.75 },
              '& h4': { fontSize: '0.95rem', fontWeight: 600, color: '#374151', mt: 2, mb: 0.5 },
              '& p': { fontSize: '0.9rem', lineHeight: 1.75, color: '#374151', mb: 1 },
              '& ul': { pl: 2.5, mb: 1 },
              '& li': { fontSize: '0.9rem', lineHeight: 1.75, color: '#374151', mb: 0.25 },
              '& strong': { color: '#111827', fontWeight: 600 },
              '& em': { color: '#6b7280' },
              '& blockquote': { borderLeft: '3px solid #0d9488', pl: 2, ml: 0, color: '#374151', fontStyle: 'italic', bgcolor: '#f0fdf4', py: 0.5, borderRadius: '0 4px 4px 0' },
              '& hr': { border: 'none', borderTop: '1px solid #e5e7eb', my: 2 },
              '& code': { bgcolor: '#f3f4f6', px: 0.75, py: 0.25, borderRadius: 1, fontSize: '0.85em', fontFamily: 'monospace' },
            }}>
              <ReactMarkdown>{interviewScriptText}</ReactMarkdown>
            </Box>
          </Paper>
        </Box>
      </Dialog>

      <Dialog
        fullScreen
        open={showPreview}
        onClose={() => setShowPreview(false)}
        TransitionComponent={Transition}
        PaperProps={{ sx: { bgcolor: '#e0e0e0' } }}
        sx={{ zIndex: 1300 }}
        keepMounted={false}
      >
        <AppBar sx={{ position: 'sticky', top: 0, bgcolor: adminColors.bg.surface, borderBottom: `1px solid ${adminColors.border.default}`, zIndex: 1100 }}>
          <Toolbar sx={{ justifyContent: 'flex-end', gap: 4 }}>
            <Box sx={{ flex: 1 }} /> {/* Spacer to push everything to the right */}
            
            <Typography sx={{ color: 'white', flexShrink: 0 }} variant="h6" component="div">
              Aperçu
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', overflowX: 'auto', justifyContent: 'flex-end' }}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                   <InputLabel sx={{ color: adminColors.text.secondary }}>Police</InputLabel>
                   <Select
                     value={previewConfig.fontFamily}
                     label="Police"
                     onChange={(e) => setPreviewConfig({...previewConfig, fontFamily: e.target.value})}
                     sx={{ ...textFieldStyles, '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
                     MenuProps={selectMenuProps}
                   >
                     <MenuItem value="Tahoma">Tahoma</MenuItem>
                     <MenuItem value="PTSans">PT Sans</MenuItem>
                     <MenuItem value="TimesNewRoman">Times New Roman</MenuItem>
                     <MenuItem value="Helvetica">Helvetica</MenuItem>
                   </Select>
                 </FormControl>

                 <FormControl size="small" sx={{ minWidth: 100 }}>
                   <InputLabel sx={{ color: adminColors.text.secondary }}>Taille</InputLabel>
                   <Select
                     value={previewConfig.fontSize}
                     label="Taille"
                     onChange={(e) => setPreviewConfig({...previewConfig, fontSize: Number(e.target.value)})}
                     sx={{ ...textFieldStyles, '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
                     MenuProps={selectMenuProps}
                   >
                     {[9, 10, 11, 12, 13, 14].map(s => (
                       <MenuItem key={s} value={s}>{s} pt</MenuItem>
                     ))}
                   </Select>
                 </FormControl>

                 <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 1, p: 0.5, px: 1 }}>
                    <Typography variant="caption" sx={{ color: 'white' }}>Couleur</Typography>
                    <input 
                      type="color" 
                      value={previewConfig.primaryColor}
                      onChange={(e) => setPreviewConfig({...previewConfig, primaryColor: e.target.value, headingColor: e.target.value})}
                      style={{ border: 'none', width: '30px', height: '30px', cursor: 'pointer', backgroundColor: 'transparent' }}
                    />
                 </Box>

                 <Button 
                   size="small"
                   variant={previewConfig.boldHeadings ? "contained" : "outlined"}
                   onClick={() => setPreviewConfig({...previewConfig, boldHeadings: !previewConfig.boldHeadings})}
                   startIcon={<BoldIcon />}
                   sx={{ 
                     borderColor: 'rgba(255,255,255,0.3)',
                     color: previewConfig.boldHeadings ? 'white' : 'rgba(255,255,255,0.7)',
                     bgcolor: previewConfig.boldHeadings ? adminColors.accent.teal : 'transparent',
                     '&:hover': {
                        borderColor: adminColors.accent.teal,
                        bgcolor: previewConfig.boldHeadings ? adminColors.accent.tealDark : 'rgba(255,255,255,0.05)'
                     }
                   }}
                 >
                   Gras
                 </Button>

                 <Button 
                    variant="contained" 
                    onClick={handleExportPDF}
                    startIcon={<PdfIcon />}
                    sx={{ bgcolor: adminColors.accent.teal, '&:hover': { bgcolor: adminColors.accent.tealDark } }}
                  >
                    PDF
                  </Button>
            </Box>

            <Button color="inherit" onClick={() => setShowPreview(false)} sx={{ ml: 4, border: '1px solid rgba(255,255,255,0.3)' }}>
              Fermer
            </Button>
          </Toolbar>
        </AppBar>
        
        {error && (
          <Alert severity="error" sx={{ m: 2, bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.2)' }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Box sx={{ p: 4, display: 'flex', justifyContent: 'flex-start', minHeight: 'calc(100vh - 64px)', overflow: 'auto', flexDirection: 'column', alignItems: 'center' }}>
            <Paper elevation={5} sx={{ width: '210mm', minHeight: 'auto', bgcolor: 'white', p: 0, overflow: 'visible', mb: 4 }}>
                <CVPreview content={adaptedText} config={previewConfig} sections={cvSections} onSectionsChange={handleSectionsChange} />
            </Paper>
        </Box>
      </Dialog>

      <Dialog open={isSaveDialogOpen} onClose={() => setIsSaveDialogOpen(false)} PaperProps={{ sx: { bgcolor: adminColors.bg.card, color: 'white' } }}>
        <DialogTitle>Sauvegarder le prompt</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nom du prompt"
            value={newPromptName}
            onChange={(e) => setNewPromptName(e.target.value)}
            sx={{ ...textFieldStyles, mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsSaveDialogOpen(false)} sx={{ color: adminColors.text.secondary }}>Annuler</Button>
          <Button onClick={handleSavePrompt} variant="contained" sx={buttonStyles.primary} disabled={!newPromptName || isSaving}>
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={isSaveCVDialogOpen} onClose={() => setIsSaveCVDialogOpen(false)} PaperProps={{ sx: { bgcolor: adminColors.bg.card, color: 'white' } }}>
        <DialogTitle>Sauvegarder dans la bibliothèque</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nom du CV (ex: CV Développeur Senior)"
            value={newCVName}
            onChange={(e) => setNewCVName(e.target.value)}
            sx={{ ...textFieldStyles, mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setIsSaveCVDialogOpen(false)} sx={{ color: adminColors.text.secondary }}>Annuler</Button>
          <Button onClick={handleSaveCV} variant="contained" sx={buttonStyles.primary} disabled={!newCVName || isSaving}>
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminCVAdaptation;
